import { useCallback, useRef } from 'react'
import {
  clusterScenesWithAudio,
  generateSceneAudio
} from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage audio clustering with scene structure processing
 * Handles clustering initiation and status polling
 */
export const useAudioClustering = ({
  formData,
  setFormData,
  formatSceneObj,
  videoId
} = {}) => {
  const {
    isPolling: isClusteringAudio,
    progress: audioClusteringProgress,
    status: audioClusteringStatus,
    startPolling,
    stopPolling
  } = useTaskPoller({
    pollInterval: POLL_INTERVAL,
    continuePollingOnError: false
  })

  /**
   * Format shot list for clustering API
   */
  const formatShotListForClustering = useCallback(shotList => {
    const sceneStructure = {}

    shotList.forEach((scene, index) => {
      const sceneKey = `scene ${index + 1}`
      const shotsObj = {}

      scene.shots.forEach((shot, sIndex) => {
        const shotKey = `shot ${sIndex + 1}`
        shotsObj[shotKey] = {
          ...shot,
          title: shot.title || '',
          shot_direction: shot.shot_direction || '',
          voiceover_text: shot.voiceover_text || '',
          initial_image_prompt: shot.initial_image_prompt || '',
          video_prompt: shot.video_prompt || '',
          scenery: shot.scenery || '',
          location: shot.location || '',
          lighting: shot.lighting || '',
          dialogue: shot.dialogue || {},
          video_duration: null
        }
      })

      sceneStructure[sceneKey] = {
        ...scene,
        scene_location: scene.scene_location || '',
        aggregated_voiceover: scene.aggregated_voiceover || '',
        audio_link: null,
        narration_audio_link: null,
        dialogue_audio_link: null,
        segment_timings: [],
        audio_duration: null,
        audio_duration_estimated: true,
        estimated_audio_duration: scene.estimated_audio_duration || null,
        shots: shotsObj
      }
    })

    return sceneStructure
  }, [])

  /**
   * Process and merge clustering results into formData
   */
  const processClusteringResults = useCallback(
    sceneData => {
      console.log('Audio clustering results received:', sceneData)
      setFormData(prev => {
        const currentShotList = [...(prev.shotList || [])]

        // Merge response data into existing shot list
        const updatedShotList = currentShotList.map((scene, index) => {
          let responseScene = null

          if (Array.isArray(sceneData)) {
            responseScene = sceneData[index]
          } else {
            const sceneKey = `scene ${index + 1}`
            responseScene =
              sceneData[sceneKey] || Object.values(sceneData)[index]
          }

          if (!responseScene) return scene

          // Create updated scene object
          const updatedScene = {
            ...scene,
            audio_link: responseScene.audio_link || scene.audio_link,
            narration_audio_link:
              responseScene.narration_audio_link || scene.narration_audio_link,
            dialogue_audio_link:
              responseScene.dialogue_audio_link || scene.dialogue_audio_link,
            segment_timings:
              responseScene.segment_timings || scene.segment_timings || [],
            audio_duration:
              responseScene.audio_duration || scene.audio_duration,
            scene_location:
              responseScene.scene_location || scene.scene_location,
            aggregated_voiceover:
              responseScene.aggregated_voiceover || scene.aggregated_voiceover,
            audio_duration_estimated: false // We now have real duration
          }

          // Update shots with video_duration from response
          if (responseScene.shots) {
            updatedScene.shots = scene.shots.map((shot, sIndex) => {
              let responseShot = null

              if (Array.isArray(responseScene.shots)) {
                responseShot = responseScene.shots[sIndex]
              } else {
                const shotKey = `shot ${sIndex + 1}`
                responseShot =
                  responseScene.shots[shotKey] ||
                  Object.values(responseScene.shots)[sIndex]
              }

              if (!responseShot) return shot

              return {
                ...shot,
                video_duration:
                  responseShot.video_duration || shot.video_duration
              }
            })
          }

          return updatedScene
        })

        return {
          ...prev,
          shotList: updatedShotList,
          shotListWithAudio: updatedShotList, // Also update audio version
          audio: {
            ...prev.audio,
            status: 'completed'
          }
        }
      })
    },
    [setFormData]
  )

  /**
   * Handle fallback when clustering fails
   */
  const handleClusteringError = useCallback(
    originalShotsData => {
      if (originalShotsData && formatSceneObj) {
        try {
          const fallbackScenes = formatSceneObj(originalShotsData)
          setFormData(prev => ({
            ...prev,
            shotList: fallbackScenes,
            audio: {
              ...prev.audio,
              status: 'failed'
            }
          }))
        } catch (fallbackErr) {
          console.error('Fallback formatting failed', fallbackErr)
        }
      } else {
        setFormData(prev => ({
          ...prev,
          audio: {
            ...prev.audio,
            status: 'failed'
          }
        }))
      }
    },
    [formatSceneObj, setFormData]
  )

  const isInitiatingRef = useRef(false)
  const lastPayloadRef = useRef(null)
  const initiatedTaskIdRef = useRef(null)

  const initiateAudioClustering = useCallback(
    async (shotList, fullShotResponse, options = {}) => {
      // Check if we are already initiating a request
      if (isInitiatingRef.current) {
        console.log(
          'Audio clustering initiation already in progress, skipping...'
        )
        return
      }

      // Check if we have a selected voice first
      // Priority: passed voiceId > options.voiceId > formData.selectedVoice
      const selectedVoice =
        options.voiceId || options.voice_id || formData?.selectedVoice

      if (!selectedVoice) {
        console.warn('No voice selected, skipping audio clustering')
        return
      }

      const voiceIdToUse =
        typeof selectedVoice === 'string'
          ? selectedVoice
          : selectedVoice.id || selectedVoice.voice_id

      // Create payload to check for redundancy
      const currentPayload = JSON.stringify({
        scene_structure: shotList,
        narrator_voice: voiceIdToUse,
        video_id: videoId
      })

      // Skip if payload is identical to last one AND we are currently clustering
      if (currentPayload === lastPayloadRef.current && isClusteringAudio) {
        console.log(
          'Duplicate audio clustering request with same data, skipping...'
        )
        return
      }

      isInitiatingRef.current = true

      try {
        const voice_map = {
          narrator: voiceIdToUse,
          ...formData.characterVoices
        }

        const payload = {
          scene_structure: shotList,
          voice_id: voiceIdToUse,
          video_id: videoId,
          video_mode: formData.audioMode
        }

        lastPayloadRef.current = currentPayload

        const res = await clusterScenesWithAudio(payload)
        const taskId = res?.data?.task_id

        if (taskId) {
          // Track this task ID so resumeAudioPolling won't duplicate polling
          initiatedTaskIdRef.current = taskId

          // Immediately update formData to persist the audio task ID to the backend
          setFormData(prev => ({
            ...prev,
            audio: {
              id: taskId,
              status: 'pending'
            }
          }))

          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            processClusteringResults(result.output)
          } else if (result && result.status === 'success') {
            // Success but no output - still mark as completed to prevent retrigger
            setFormData(prev => ({
              ...prev,
              audio: { ...prev.audio, status: 'completed' }
            }))
          } else if (result && result.status === 'failed') {
            handleClusteringError(fullShotResponse)
          } else {
            // Cancelled or unexpected - mark as failed to prevent retrigger
            setFormData(prev => ({
              ...prev,
              audio: { ...prev.audio, status: 'failed' }
            }))
          }
        } else {
          lastPayloadRef.current = null // Reset on failure
        }
      } catch (error) {
        console.error('Audio clustering failed:', error)
        handleClusteringError(fullShotResponse)
        lastPayloadRef.current = null // Reset on failure
      } finally {
        isInitiatingRef.current = false
      }
    },
    [
      formData?.selectedVoice,
      formData.characterVoices,
      formData.characterMap,
      videoId,
      isClusteringAudio,
      startPolling,
      processClusteringResults,
      handleClusteringError
    ]
  )

  function flattenCharacters(obj) {
    const result = {}

    for (const [key, value] of Object.entries(obj)) {
      result[key] =
        typeof value === 'object' && value !== null
          ? value.name || value.character_id
          : value
    }

    return result
  }

  const resumeAudioPolling = useCallback(
    async taskId => {
      if (!taskId || isClusteringAudio) return

      // Skip if this task was just initiated in this session
      // (initiateAudioClustering already started polling for it)
      if (initiatedTaskIdRef.current === taskId) return

      try {
        const result = await startPolling(taskId)
        if (result && result.status === 'success' && result.output) {
          processClusteringResults(result.output)
        } else if (result && result.status === 'success') {
          // Success but no output - still mark as completed
          setFormData(prev => ({
            ...prev,
            audio: { ...prev.audio, status: 'completed' }
          }))
        } else if (result && result.status === 'failed') {
          handleClusteringError()
        } else {
          // Cancelled or unexpected - mark as failed
          setFormData(prev => ({
            ...prev,
            audio: { ...prev.audio, status: 'failed' }
          }))
        }
      } catch (error) {
        console.error('Error resuming audio polling:', error)
        handleClusteringError()
      }
    },
    [
      isClusteringAudio,
      startPolling,
      processClusteringResults,
      handleClusteringError
    ]
  )

  /**
   * Reset audio clustering state
   */
  const resetAudioClustering = useCallback(() => {
    stopPolling()
  }, [stopPolling])

  return {
    // State
    isClusteringAudio,
    audioClusteringProgress,
    audioClusteringStatus,

    // Actions
    initiateAudioClustering,
    formatShotListForClustering,
    resumeAudioPolling,
    resetAudioClustering
  }
}

export default useAudioClustering
